// src/pages/Register.js
import React, { useState, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';
import GlobalTable from '../components/GlobalTable';
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';

const Register = () => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        confirmPassword:'',
        parent:'',
        organization: '',
        department: '',
        is_admin: false,
        can_record_payments: false,
        can_approve_purchase_orders: false,
    });
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [dialogName, setDialogName] = useState('Create User');
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState({});
    const [pinDialogVisible, setPinDialogVisible] = useState(false);
    const [pinTargetUser, setPinTargetUser] = useState(null);
    const [newPin, setNewPin] = useState('');

    // ── Role assignment state ─────────────────────────────────────────────────
    const [allRoles, setAllRoles] = useState([]);               // all roles in the org
    const [userRoles, setUserRoles] = useState([]);             // role IDs currently assigned
    const [rolesLoading, setRolesLoading] = useState(false);
    const [rolesSaving, setRolesSaving] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null); // user whose roles we're editing

    // Fetch departments and organizations
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const userMe = await axiosInstance.get('/api/user/me/');
                setCurrentUser(userMe.data);
                const isGfadmin = userMe.data?.username === 'gfadmin';

                const userResult = isGfadmin
                    ? await axiosInstance.get('/api/register/')
                    : await axiosInstance.get('/api/orguser/');

                setUsers(userResult.data);

                const departmentResult = await axiosInstance.get('/api/departments/');
                setDepartments(departmentResult.data);

                const orgResult = await axiosInstance.get('/api/organizations/');
                setOrganizations(orgResult.data);

                // Load all roles for the org (used in the role picker)
                const rolesResult = await axiosInstance.get('/api/roles/');
                setAllRoles(rolesResult.data);

                setLoading(false);
            } catch (error) {
                handleApiError(error);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // ── When a user row is opened, fetch their current roles ─────────────────
    const fetchUserRoles = async (userId) => {
        setRolesLoading(true);
        try {
            const res = await axiosInstance.get(`/api/users/${userId}/roles/`);
            // effective_permissions may be ['*'] for admin — roles is the list we want
            setUserRoles(res.data.roles.map(r => r.id));
        } catch (error) {
            handleApiError(error);
            setUserRoles([]);
        } finally {
            setRolesLoading(false);
        }
    };

    // ── Save role changes via bulk assign ─────────────────────────────────────
    const handleSaveRoles = async () => {
        if (!selectedUserId) return;
        setRolesSaving(true);
        try {
            await axiosInstance.post(`/api/users/${selectedUserId}/roles/bulk/`, {
                role_ids: userRoles,
            });
            handleApiSuccess('Permissions updated successfully!');
        } catch (error) {
            handleApiError(error);
        } finally {
            setRolesSaving(false);
        }
    };

    // ── Toggle a role on/off in local state ───────────────────────────────────
    const toggleRole = (roleId) => {
        setUserRoles(prev =>
            prev.includes(roleId)
                ? prev.filter(id => id !== roleId)
                : [...prev, roleId]
        );
    };

    const handleChange = (e, field) => {
        const value = e.target ? e.target.value : e.value;
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleRowClick = (dep) => {
        setFormData({
            ...dep,
            parent: dep.employee?.parent || '',
            can_record_payments: dep.employee?.can_record_payments || false,
            can_approve_purchase_orders: dep.employee?.can_approve_purchase_orders || false,
        });
        setSelectedUserId(dep.id);
        setIsReadOnly(true);
        setDialogVisible(true);
        setDialogName('User Details');
        fetchUserRoles(dep.id);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            handleApiError({Error:"Passwords do not match."});
            return;
        }
        const payload = {
            // Login username is always the email — the backend enforces
            // this too (UserSerializer.create), this just keeps the
            // payload honest about what will actually be saved.
            username: formData.email,
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            password: formData.password,
            employee: {
                department: formData.department,
                organization: formData.organization,
                parent: formData.parent,
                is_admin: formData.is_admin,
                can_record_payments: !!formData.can_record_payments,
                can_approve_purchase_orders: !!formData.can_approve_purchase_orders,
            },
        };

        try {
            const response = await axiosInstance.post('/api/register/', payload);
            handleApiSuccess(`User: ${response.data.username} registered successfully!`);
            const updatedUsers = currentUser?.username === 'gfadmin'
                ? await axiosInstance.get('/api/register/')
                : await axiosInstance.get('/api/orguser/');
            setUsers(updatedUsers.data);
            setFormData({
                username: '', email: '', password: '', organization: '',
                department: '', first_name: '', last_name: '', confirmPassword: '',
                is_admin: false, can_record_payments: false, can_approve_purchase_orders: false,
            });
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleRowEditComplete = async (e) => {
        const { newData, index } = e;
        const payload = {
            username: newData.username,
            first_name: newData.first_name,
            last_name: newData.last_name,
            email: newData.email,
            employee: {
                organization: newData.organization,
                department: newData.department,
                parent: newData.parent,
                is_admin: newData.is_admin,
                can_record_payments: newData.can_record_payments || false,
                can_approve_purchase_orders: newData.can_approve_purchase_orders || false,
            },
        };
        try {
            const response = await axiosInstance.put(`/api/register/${newData.id}/`, payload);
            const updatedusers = [...users];
            updatedusers[index] = response.data;
            setUsers(updatedusers);
            handleApiSuccess('User updated successfully!');
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleResetPassword = async (rowData, newPassword) => {
        try {
            const payload = {
                password: newPassword,
                employee: { password_change_required: true },
            };
            await axiosInstance.put(`/api/pwreset/${rowData.id}/`, payload);
            handleApiSuccess(`Password reset successfully for ${rowData.username}`);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getOrganizationName = React.useCallback((orgId) => {
        const organization = organizations.find(org => org.id === orgId);
        return organization ? organization.organization : 'N/A';
    }, [organizations]);

    const getParDep = React.useCallback((depId) => {
        const pardep = departments.find(dep => dep.id === depId);
        return pardep ? pardep.department : '';
    }, [departments]);

    const getUser = React.useCallback((userId) => {
        const paruser = users.find(user => user.id === userId);
        return paruser ? paruser.username : '';
    }, [users]);

    const columns = React.useMemo(
        () => [
            {
                Header: 'User Name',
                accessor: 'username',
                sortable: true,
                filtered: true,
                bodyTemplate: (rowData) => (
                    <a
                        href="/register/#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleRowClick(rowData);
                        }}
                    >
                        {rowData.username}
                    </a>
                ),
            },
            {
                Header: 'First Name',
                accessor: 'first_name',
                sortable: true,
                filtered: true,
            },
            {
                Header: 'Last Name',
                accessor: 'last_name',
                sortable: true,
                filtered: true,
            },
            {
                Header: 'Parent User',
                accessor: 'parent',
                filtered: true,
                bodyTemplate: (rowData) => getUser(rowData.employee.parent),
            },
            {
                Header: 'Organization',
                accessor: 'organization',
                sortable: true,
                filtered: true,
                bodyTemplate: (rowData) => getOrganizationName(rowData.employee.organization),
            },
            {
                Header: 'Admin User',
                accessor: 'is_admin',
                sortable: true,
                filtered: true,
                bodyTemplate: (rowData) => (
                    <Checkbox checked={rowData.is_admin === true || rowData.is_admin === 'Y'} disabled />
                ),
            },
            {
                Header: 'Supplier Payments',
                accessor: 'can_record_payments',
                sortable: true,
                filtered: true,
                bodyTemplate: (rowData) => (
                    <Checkbox checked={rowData.can_record_payments === true || rowData.can_record_payments === 'Y'} disabled />
                ),
            },
            {
                Header: 'PO Approver',
                accessor: 'can_approve_purchase_orders',
                sortable: true,
                filtered: true,
                bodyTemplate: (rowData) => (
                    <Checkbox checked={rowData.can_approve_purchase_orders === true || rowData.can_approve_purchase_orders === 'Y'} disabled />
                ),
            },
            {
                Header: 'Department',
                accessor: 'department',
                sortable: true,
                filtered: true,
                bodyTemplate: (rowData) => getParDep(rowData.employee.department),
            },
            {
                Header: 'Email',
                accessor: 'email',
                sortable: true,
                filtered: true,
            },
            {
                Header: 'Date Joined',
                accessor: 'date_joined',
                sortable: true,
                filtered: true,
                dataType: 'date',
                readOnly: true,
            },
            {
                Header: 'Last Login Date',
                accessor: 'last_login',
                sortable: true,
                filtered: true,
                dataType: 'date',
                readOnly: true,
            },
        ],
        [getOrganizationName, getParDep]
    );

    const data = React.useMemo(
        () => users.map(users => ({
            ...users,
            organizationName: getOrganizationName(users.employee.organization),
            depName: getParDep(users.employee.department),
            organization: users.employee.organization,
            department: users.employee.department,
            parent: users.employee.parent,
            is_admin: users.employee.is_admin ?? false,
            can_record_payments: users.employee.can_record_payments ?? false,
            can_approve_purchase_orders: users.employee.can_approve_purchase_orders ?? false,
        })),
        [users, getOrganizationName, getParDep]
    );

    const handleAddClick = () => {
        setFormData({
            username: '',
            email: '',
            first_name: '',
            last_name: '',
            password: '',
            confirmPassword: '',
            organization: currentUser?.organization,
            department: '',
            is_admin: false,
            can_record_payments: false,
            can_approve_purchase_orders: false,
        });
        setSelectedUserId(null);
        setUserRoles([]);
        setIsReadOnly(false);
        setDialogName('Create User');
        setDialogVisible(true);
    };

    const headerAction = (
        <Button type="button" icon="pi pi-plus" label=" Add User" outlined onClick={handleAddClick} className="w-full sm:w-auto" />
    );

    return (
        <div>
            <div className="containers">
                <GlobalTable
                    columns={columns}
                    data={data}
                    tabletitle="User List"
                    handleResetPassword={handleResetPassword}
                    headerAction={headerAction}
                    loading={loading}
                    customEditors={{
                        parent: (options) => (
                            <Dropdown
                                value={options.rowData.employee.parent}
                                options={users}
                                optionLabel="username"
                                optionValue="id"
                                onChange={(e) => { options.editorCallback(options.rowData.employee.parent = e.value) }}
                                placeholder="Select Parent"
                            />
                        ),
                        organization: (options) => (
                            <Dropdown
                                value={options.rowData.employee.organization}
                                options={organizations}
                                optionLabel="organization"
                                optionValue="id"
                                onChange={(e) => { options.editorCallback(options.rowData.employee.organization = e.value) }}
                                placeholder="Select Organization"
                            />
                        ),
                        department: (options) => (
                            <Dropdown
                                value={options.rowData.employee.department}
                                options={departments}
                                optionLabel="department"
                                optionValue="id"
                                onChange={(e) => { options.editorCallback(options.rowData.employee.department = e.value) }}
                                placeholder="Select Department"
                            />
                        ),
                        is_admin: (options) => (
                            <Checkbox
                                inputId="is_admin"
                                checked={options.rowData.is_admin === true || options.rowData.is_admin === 'Y'}
                                onChange={(e) => options.editorCallback(e.checked)}
                            />
                        ),
                        can_record_payments: (options) => (
                            <Checkbox
                                inputId="can_record_payments"
                                checked={options.rowData.can_record_payments === true}
                                onChange={(e) => options.editorCallback(e.checked)}
                            />
                        ),
                        can_approve_purchase_orders: (options) => (
                            <Checkbox
                                inputId="can_approve_purchase_orders"
                                checked={options.rowData.can_approve_purchase_orders === true}
                                onChange={(e) => options.editorCallback(e.checked)}
                            />
                        ),
                    }}
                    onRowEditComplete={handleRowEditComplete}
                    showEditColumn={false}
                    userManager={true}
                />
            </div>

            {/* ── User Create / View Dialog ─────────────────────────────────── */}
            <Dialog
                header={dialogName}
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                modal
                style={{ width: '45rem', borderRadius: '12px', padding: '.5rem' }}
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                className="p-fluid"
            >
                <form id="dialogForm" onSubmit={handleSubmit}>
                    <div style={{ borderRadius: '12px', padding: '1rem' }}>
                        <div className="formgrid grid align-items-end">
                            <div className="field col">
                                <label htmlFor="username" className="font-bold">Username:</label>
                                <InputText
                                    name="username"
                                    id="username"
                                    // Login username is always the email — see
                                    // gf.serializers.UserSerializer.create, which
                                    // overwrites whatever's sent here anyway. Shown
                                    // read-only, mirrored live from Email below, so
                                    // there's no separate value to hand-type or drift
                                    // out of sync with what actually gets saved.
                                    value={dialogName === 'Create User' ? formData.email : formData.username}
                                    placeholder='Same as email'
                                    autoComplete="new-username"
                                    style={{ borderRadius: '6px' }}
                                    disabled
                                />
                            </div>
                            <div className="field col flex align-items-end" style={{ marginBottom: '0.5rem' }}>
                                <div className="flex align-items-center">
                                    <Checkbox
                                        inputId="is_admin"
                                        checked={formData.is_admin === true || formData.is_admin === 'Y'}
                                        onChange={(e) => handleChange({ target: { value: e.checked } }, 'is_admin')}
                                        disabled={isReadOnly}
                                    />
                                    <label htmlFor="is_admin" className="ml-2 mb-0">Admin User</label>
                                </div>
                                <div className="flex align-items-center">
                                    <Checkbox
                                        inputId="can_record_payments"
                                        checked={formData.can_record_payments === true}
                                        onChange={(e) => handleChange({ target: { value: e.checked } }, 'can_record_payments')}
                                        disabled={isReadOnly}
                                    />
                                    <label htmlFor="can_record_payments" className="ml-2 mb-0">Supplier Payments</label>
                                </div>
                                <div style={{ marginLeft: 12 }} className="flex align-items-center">
                                    <Checkbox
                                        inputId="can_approve_purchase_orders"
                                        checked={formData.can_approve_purchase_orders === true}
                                        onChange={(e) => handleChange({ target: { value: e.checked } }, 'can_approve_purchase_orders')}
                                        disabled={isReadOnly}
                                    />
                                    <label htmlFor="can_approve_purchase_orders" className="ml-2 mb-0">PO Approver</label>
                                </div>
                            </div>
                        </div>
                        <div className="formgrid grid">
                            <div className="field col">
                                <label htmlFor='first_name' className="font-bold">First Name:</label>
                                <InputText
                                    name="first_name"
                                    id="first_name"
                                    value={formData.first_name}
                                    onChange={(e) => handleChange(e, 'first_name')}
                                    placeholder='Enter First Name'
                                    disabled={isReadOnly}
                                />
                            </div>
                            <div className="field col">
                                <label htmlFor='last_name' className="font-bold">Last Name:</label>
                                <InputText
                                    name="last_name"
                                    id="last_name"
                                    value={formData.last_name}
                                    onChange={(e) => handleChange(e, 'last_name')}
                                    placeholder='Enter Last Name'
                                    required
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>
                        <div className="formgrid grid">
                            <div className="field col">
                                <label htmlFor='email' className="font-bold">Email:</label>
                                <InputText
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange(e, 'email')}
                                    placeholder='Enter User Email'
                                    required
                                    disabled={isReadOnly}
                                />
                            </div>
                            <div className="field col">
                                <label htmlFor='parent' className="font-bold">Parent User:</label>
                                <Dropdown
                                    id="parent"
                                    value={formData.parent}
                                    options={users}
                                    optionLabel="username"
                                    optionValue="id"
                                    onChange={(e) => handleChange(e, 'parent')}
                                    placeholder="Select Parent User"
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>
                        <div className="formgrid grid">
                            <div className="field col">
                                <label htmlFor="organization" className="font-bold">Organization:</label>
                                <Dropdown
                                    id="organization"
                                    value={formData.organization}
                                    options={organizations}
                                    optionLabel="organization"
                                    optionValue="id"
                                    onChange={(e) => handleChange(e, 'organization')}
                                    placeholder="Select Organization"
                                    required
                                    disabled={isReadOnly || currentUser?.username !== 'gfadmin'}
                                />
                            </div>
                            <div className="field col">
                                <label htmlFor="department" className="font-bold">Department:</label>
                                <Dropdown
                                    id="department"
                                    value={formData.department}
                                    options={departments}
                                    optionLabel="department"
                                    optionValue="id"
                                    onChange={(e) => handleChange(e, 'department')}
                                    placeholder="Select Department"
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>
                        <div className="formgrid grid">
                            <div className="field col">
                                <label htmlFor="password" className="font-bold">Password:</label>
                                <Password
                                    id="password"
                                    value={formData.password}
                                    onChange={(e) => handleChange(e, 'password')}
                                    placeholder="Enter Password"
                                    autoComplete='new-password'
                                    toggleMask
                                    required
                                    disabled={isReadOnly}
                                />
                            </div>
                            <div className="field col">
                                <label htmlFor="confirmPassword" className="font-bold">Confirm Password:</label>
                                <Password
                                    id="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={(e) => handleChange(e, 'confirmPassword')}
                                    placeholder="Confirm Password"
                                    toggleMask
                                    required
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <Button label="Save" icon="pi pi-check" form="dialogForm" type='submit' style={{ borderRadius: "6px", maxWidth: "120px" }} disabled={isReadOnly} />
                        <Button label="Cancel" icon="pi pi-times" outlined onClick={(e) => { setDialogVisible(false); e.preventDefault(); }} style={{ borderRadius: "6px", maxWidth: "120px" }} />
                    </div>
                    <div style={{ margin: "6px", display: "flex", justifyContent: "center" }}>
                        <Button
                            label="Set / Reset Operator PIN"
                            icon="pi pi-key"
                            className="p-button-warning"
                            style={{ borderRadius: "6px", maxWidth: "220px" }}
                            onClick={(e) => {
                                e.preventDefault();
                                setPinTargetUser(formData);
                                setPinDialogVisible(true);
                            }}
                        />
                    </div>
                </form>

                {/* ── Role Assignment Panel ─────────────────────────────────────
                    Shown only when viewing an existing user (isReadOnly = true).
                    Kept outside the <form> so the Save button doesn't trigger it.
                    Admin users show a note instead — their '*' wildcard already
                    grants full access regardless of roles.
                ─────────────────────────────────────────────────────────────── */}
                {isReadOnly && selectedUserId && (
                    <div style={{
                        margin: '0 1rem 1rem 1rem',
                        padding: '1rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        background: '#f9fafb',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                Assigned Permissions
                            </span>
                            <Button
                                label="Save Permissions"
                                icon="pi pi-check"
                                className="p-button-sm"
                                style={{ borderRadius: '6px' }}
                                loading={rolesSaving}
                                disabled={rolesLoading || formData.is_admin === true || formData.is_admin === 'Y'}
                                onClick={handleSaveRoles}
                            />
                        </div>

                        {/* Admin bypass notice */}
                        {(formData.is_admin === true || formData.is_admin === 'Y') && (
                            <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>
                                Admin users have full access to all features. Permission assignment is not needed.
                            </p>
                        )}

                        {/* Role checkboxes */}
                        {!(formData.is_admin === true || formData.is_admin === 'Y') && (
                            rolesLoading ? (
                                <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Loading permissions...</p>
                            ) : allRoles.length === 0 ? (
                                <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                                    No permissions found. Create permissions first under <strong>Admin Organizations → Permissions</strong>.
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem' }}>
                                    {allRoles.map(role => (
                                        <div key={role.id} className="flex align-items-center" style={{ minWidth: '140px' }}>
                                            <Checkbox
                                                inputId={`role-${role.id}`}
                                                checked={userRoles.includes(role.id)}
                                                onChange={() => toggleRole(role.id)}
                                            />
                                            <label
                                                htmlFor={`role-${role.id}`}
                                                className="ml-2"
                                                style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                                title={role.description || ''}
                                            >
                                                {role.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                )}
            </Dialog>

            {/* ── Operator PIN Dialog ───────────────────────────────────────── */}
            <Dialog
                header="Set Operator PIN"
                visible={pinDialogVisible}
                modal
                style={{ width: '24rem' }}
                onHide={() => {
                    setPinDialogVisible(false);
                    setNewPin('');
                }}
            >
                <div className="p-fluid">
                    <label className="font-bold mb-2">New PIN</label>
                    <Password
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value)}
                        feedback={false}
                        maxLength={4}
                        toggleMask
                        placeholder="Enter new PIN"
                    />
                    <div className="flex justify-content-end gap-2 mt-4">
                        <Button
                            label="Cancel"
                            className="p-button-text"
                            onClick={() => setPinDialogVisible(false)}
                        />
                        <Button
                            label="Save PIN"
                            icon="pi pi-check"
                            onClick={async () => {
                                await axiosInstance.post(`/api/operator/set-pin/`, {
                                    user_id: pinTargetUser.id,
                                    pin: newPin,
                                });
                                handleApiSuccess('Operator PIN updated');
                                setPinDialogVisible(false);
                                setNewPin('');
                            }}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default Register;